import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { registerAtomicTransaction, reportError } from 'utils/error'

export enum WebSocketReadyState {
  CONNECTING,
  OPEN,
  CLOSING,
  CLOSED,
}

export type OnCloseHandler = ((this: WebSocket, ev: CloseEvent) => any) | null
export type OnErrorHandler = ((this: WebSocket, ev: Event) => any) | null
export type OnMessageHandler = ((this: WebSocket, ev: MessageEvent<any>) => any) | null
export type OnOpenHandler = ((this: WebSocket, ev: Event) => any) | null

// As per https://websockets.spec.whatwg.org/#websocket
// https://www.rfc-editor.org/rfc/rfc6455.html#section-7.1.5
export class DebugWebSocket implements WebSocket {
  readonly CLOSED = WebSocketReadyState.CLOSED
  readonly CLOSING = WebSocketReadyState.CLOSING
  readonly CONNECTING = WebSocketReadyState.CONNECTING
  readonly OPEN = WebSocketReadyState.OPEN

  // DebugWebSocket props
  _onclose?: OnCloseHandler
  _onerror?: OnErrorHandler
  _onmessage?: OnMessageHandler
  _onopen?: OnOpenHandler
  _disconnected: WebSocketReadyState = WebSocketReadyState.CLOSED
  _websocket: WebSocket
  _listeners: Record<keyof WebSocketEventMap, Set<Function>> = {
    close: new Set<Function>(),
    error: new Set<Function>(),
    message: new Set<Function>(),
    open: new Set<Function>(),
  }
  _errored: boolean = false
  constructor(connectionString: string, ...protocols: string[]) {
    try {
      this._websocket = new WebSocket(connectionString, ...protocols)
      this._websocket.onclose = this.onClose
      this._websocket.onerror = this.onError
    } catch (error) {
      const searchTags =
        error instanceof Error
          ? {
              errorName: error.constructor.name,
              message: error.message,
            }
          : undefined
      reportError(
        'WebSocketCreationError',
        {
          severity: 'error',
          metaData: error,
        },
        searchTags,
      )
      throw error
    }
  }

  // PROPS
  get binaryType(): BinaryType {
    return this._websocket.binaryType
  }
  set binaryType(binaryType: BinaryType) {
    this._websocket.binaryType = binaryType
  }

  // READONLY-PROPS
  get bufferedAmount(): number {
    return this._websocket.bufferedAmount
  }
  get extensions(): string {
    return this._websocket.extensions
  }
  get protocol(): string {
    return this._websocket.protocol
  }
  get readyState(): WebSocketReadyState {
    return this._websocket.readyState
  }
  get url(): string {
    return this._websocket.url
  }

  // EVENT HANDLERS
  get onclose(): OnCloseHandler {
    return this._websocket.onclose
  }
  set onclose(onclose: OnCloseHandler) {
    this._onclose = onclose
  }
  // https://www.rfc-editor.org/rfc/rfc6455.html#section-7.4.1
  private onClose = (ev: CloseEvent) => {
    registerAtomicTransaction('WebSocketClosing', {
      wasClean: ev.wasClean + '',
      reason: ev.reason,
      code: ev.code,
      afterError: this._errored + '',
      sampleRate: 0.5,
    })
    this._onclose?.call(this._websocket, ev)
  }

  get onerror(): OnErrorHandler {
    return this._websocket.onerror
  }
  set onerror(onerror: OnErrorHandler) {
    this._onerror = onerror
  }
  private onError = (ev: Event) => {
    // We have no useful information in the onerror event
    // https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/error_event
    // The standard is too loose and Chrome includes no message, nor code, nor reason
    this._errored = true
  }

  get onmessage(): OnMessageHandler {
    return this._websocket.onmessage
  }
  set onmessage(onmessage: OnMessageHandler) {
    this._onmessage = onmessage
    this._websocket.onmessage = onmessage
  }
  get onopen(): OnOpenHandler {
    return this._websocket.onopen
  }
  set onopen(onopen: OnOpenHandler) {
    this._onopen = onopen
    this._websocket.onopen = onopen
  }

  close = (code?: number | undefined, reason?: string | undefined) => {
    if (code || reason) {
      registerAtomicTransaction('ClientSideWebSocketClose', { code, reason })
    }
    return this._websocket.close()
  }
  send = (data: string | ArrayBufferLike | Blob | ArrayBufferView) => {
    return this._websocket.send(data)
  }

  addEventListener = <K extends keyof WebSocketEventMap>(
    type: K,
    listener: (this: WebSocket, ev: WebSocketEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions | undefined,
  ): void => {
    this._listeners[type].add(listener)
    return this._websocket.addEventListener(type, listener, options)
  }

  removeEventListener = <K extends keyof WebSocketEventMap>(
    type: K,
    listener: (this: WebSocket, ev: WebSocketEventMap[K]) => any,
    options?: boolean | EventListenerOptions | undefined,
  ): void => {
    this._listeners[type].delete(listener)
    return this._websocket.removeEventListener(type, listener, options)
  }

  dispatchEvent = (event: Event): boolean => {
    return this._websocket.dispatchEvent(event)
  }
}

export function useDebugWebSocket() {
  const useDebugWebsocket = useFlipperFlag('cable_debug_websocket')
  if (useDebugWebsocket) {
    localStorage.setItem('cable_debug_websocket', 'true')
  } else {
    localStorage.removeItem('cable_debug_websocket')
  }
}
